import { chunk } from "lodash";

import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlightIcon from "@mui/icons-material/Flight";
import { Accordion, AccordionSummary, Button, Typography } from "@mui/material";

import FlightItem from "@/components/FlightItem";
import OptionDialog from "@/components/OptionDialog";
import usePlatform from "@/hooks/usePlatform";
import BannerCard from "@/pages/FlightSearch/BannerCard";
import { FlightItem as FlightItemType } from "@/typings";
import { SHOW_BANNER_CARD } from "@/constants/membership";
import { ProInfoUseInFlight } from "../ProInfo";

interface FlightListProps {
  data: FlightItemType[];
  showPro?: boolean;
  isNeedTopData?: boolean;
  needMore?: boolean;
  showSearchBtn?: boolean;
  needRouteBtn?: boolean;
  onOpenRouteDialog?: (v: FlightItemType) => void;
}

const FlightList = ({
  data,
  showPro = false,
  isNeedTopData = false,
  needMore = true,
  showSearchBtn,
  needRouteBtn = false,
  onOpenRouteDialog,
}: FlightListProps) => {
  const [expandKeys, setExpandKey] = useState<string[]>([]);
  const { isPc } = usePlatform();
  const pageSize = useRef(30);
  const currentPage = useRef(0);
  const chunkData = useRef<FlightItemType[][]>([]);
  const [listData, setListData] = useState<FlightItemType[]>([]);

  const [optionDialogData, setOptionDialogData] = useState<FlightItemType>();
  const alertDetailPage = ["/alert-detail", "/deals/flight", "/routes-flights"].includes(
    window.location.pathname
  );

  const handleExpandChange = useCallback(
    (key: string) => {
      if (expandKeys.includes(key)) {
        setExpandKey(expandKeys.filter((ele) => ele !== key));
      } else {
        setExpandKey([...expandKeys, key]);
      }
    },
    [expandKeys]
  );

  const handleOpenOptionDialog = useCallback((data: FlightItemType) => {
    setOptionDialogData(data);
  }, []);

  const handlePaginationChange = useCallback(() => {
    currentPage.current = currentPage.current + 1;
    setListData([...listData, ...chunkData.current[currentPage.current]]);
  }, [listData]);

  useEffect(() => {
    if (needMore) {
      chunkData.current = chunk(data, pageSize.current);
      currentPage.current = 0;
      setListData([...chunkData.current[currentPage.current]]);
      setExpandKey([]);
    } else {
      setListData(data);
    }
  }, [data, needMore]);

  const num = isNeedTopData ? 2 : 2;

  const bannerNum = 4;

  return (
    <div>
      {listData.map((ele, index) => {
        return (
          <Fragment key={`${ele.hash}`}>
            <FlightItem
              showLastSeen={alertDetailPage}
              key={`${ele.hash}`}
              data={ele}
              isExpanded={expandKeys.includes(ele.hash)}
              onExpandChange={handleExpandChange}
              onOpenOptionDialog={handleOpenOptionDialog}
              showSearchBtn={showSearchBtn}
              onOpenRouteDialog={onOpenRouteDialog}
              needRouteBtn={needRouteBtn}
            />
            {showPro &&
              ((listData.length > num && index === num - 1) ||
                (listData.length < num && index == listData.length - 1)) && <ProInfoUseInFlight />}

            {SHOW_BANNER_CARD&&
              isPc &&
              ((listData.length > bannerNum && index === bannerNum - 1) ||
                (listData.length < bannerNum && index == listData.length - 1)) && (
                <BannerCard usedWhere="flight" />
              )}
          </Fragment>
        );
      })}

      {needMore && listData.length > 0 && currentPage.current < chunkData.current.length - 1 ? (
        <Accordion
          expanded={false}
          sx={{
            boxShadow: "none",
            marginTop: "-1px",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <AccordionSummary onClick={(e) => e.stopPropagation()}>
          <Button
              className="w-[100%] h-[100%] mx-auto flex items-center justify-center"
              onClick={handlePaginationChange}
              variant="text"
              color="primary"
              startIcon={<ExpandMoreIcon />}
              endIcon={<FlightIcon className="rotate-[45deg]" />}
            >
              <Typography color="textPrimary" className="!pr-[4px]">
                More Flights{" "}
              </Typography>
            </Button>
          </AccordionSummary>
        </Accordion>
      ) : null}

      <OptionDialog
        data={optionDialogData}
        onClose={() => {
          setOptionDialogData(undefined);
        }}
      />
    </div>
  );
};

export default React.memo(FlightList);
